import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { 
  User, Mail, Award, LogOut, Settings, Bell, HelpCircle, ChevronRight,
  Trophy, Star, Medal, BookOpen, Target, TrendingUp, FileCheck, Download
} from 'lucide-react';
import { toast } from 'sonner';
import BottomNavigation from '../components/BottomNavigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentProfile() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();
  const [gamificationStats, setGamificationStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const [statsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/gamification/stats`, getAuthHeader()),
        axios.get(`${API}/points/leaderboard`, getAuthHeader())
      ]);
      setGamificationStats(statsRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch (error) {
      console.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const handleDownloadCertificate = (cert) => {
    toast.success(`Downloading certificate: ${cert.certificate_number}`);
    // In a real app, this would generate/download a PDF certificate
  };

  const menuItems = [
    { icon: Bell, label: 'Notifications', onClick: () => toast.info('Coming soon'), badge: '3' },
    { icon: Settings, label: 'Settings', onClick: () => toast.info('Coming soon') },
    { icon: HelpCircle, label: 'Help & Support', onClick: () => toast.info('Coming soon') },
  ];

  // Calculate level based on points
  const calculateLevel = (points) => {
    if (points < 100) return { level: 1, name: 'Beginner', nextLevel: 100, color: 'bg-slate-500' };
    if (points < 500) return { level: 2, name: 'Learner', nextLevel: 500, color: 'bg-green-500' };
    if (points < 1000) return { level: 3, name: 'Scholar', nextLevel: 1000, color: 'bg-blue-500' };
    if (points < 2500) return { level: 4, name: 'Expert', nextLevel: 2500, color: 'bg-purple-500' };
    if (points < 5000) return { level: 5, name: 'Master', nextLevel: 5000, color: 'bg-amber-500' };
    return { level: 6, name: 'Grandmaster', nextLevel: 10000, color: 'bg-red-500' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 pb-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const stats = gamificationStats || {};
  const levelInfo = calculateLevel(stats.total_points || 0);
  const progressToNextLevel = Math.min(((stats.total_points || 0) / levelInfo.nextLevel) * 100, 100);

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header with Profile */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="px-4 py-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center relative">
              <User className="w-8 h-8 text-white" />
              <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full ${levelInfo.color} flex items-center justify-center text-xs font-bold border-2 border-white`}>
                {levelInfo.level}
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold">{user?.name || 'Student'}</h1>
              <p className="text-white/80 text-sm flex items-center">
                <Mail className="w-3 h-3 mr-1" />
                {user?.email}
              </p>
              <div className="flex items-center space-x-2 mt-1">
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${levelInfo.color}`}>
                  {levelInfo.name}
                </span>
                <span className="text-white/80 text-xs">
                  <Star className="w-3 h-3 inline mr-1" />
                  {stats.total_points || 0} pts
                </span>
              </div>
            </div>
          </div>
          
          {/* Level Progress */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-white/80 mb-1">
              <span>Level {levelInfo.level}</span>
              <span>{stats.total_points || 0} / {levelInfo.nextLevel} pts</span>
            </div>
            <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all duration-500" 
                style={{ width: `${progressToNextLevel}%` }}
              ></div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full bg-white border border-slate-200 p-1 rounded-xl mb-4">
            <TabsTrigger 
              value="dashboard" 
              className="flex-1 rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-xs"
            >
              Dashboard
            </TabsTrigger>
            <TabsTrigger 
              value="leaderboard" 
              className="flex-1 rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-xs"
            >
              Leaderboard
            </TabsTrigger>
            <TabsTrigger 
              value="certificates" 
              className="flex-1 rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-xs"
            >
              Certificates
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-4">
            {/* Points Stats */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-xl p-4">
                <Trophy className="w-8 h-8 mb-2" />
                <p className="text-2xl font-bold">{stats.total_points || 0}</p>
                <p className="text-xs text-white/80">Total Points</p>
              </Card>
              <Card className="bg-gradient-to-br from-indigo-500 to-purple-500 text-white rounded-xl p-4">
                <Medal className="w-8 h-8 mb-2" />
                <p className="text-2xl font-bold">#{stats.rank || '-'}</p>
                <p className="text-xs text-white/80">Global Rank</p>
              </Card>
            </div>

            {/* Points Breakdown */}
            <Card className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
              <h3 className="font-bold text-slate-900 mb-3 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-indigo-600" />
                Points Breakdown
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Course Enrollments</p>
                      <p className="text-xs text-slate-500">20% of course value</p>
                    </div>
                  </div>
                  <span className="font-bold text-teal-600">+{stats.points_breakdown?.from_enrollments || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Award className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Quiz Scores</p>
                      <p className="text-xs text-slate-500">10% of marks obtained</p>
                    </div>
                  </div>
                  <span className="font-bold text-purple-600">+{stats.points_breakdown?.from_quizzes || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <FileCheck className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Course Completions</p>
                      <p className="text-xs text-slate-500">Bonus on completion</p>
                    </div>
                  </div>
                  <span className="font-bold text-amber-600">+{stats.points_breakdown?.from_courses || 0}</span>
                </div>
              </div>
            </Card>

            {/* Stats Overview */}
            <Card className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
              <h3 className="font-bold text-slate-900 mb-3 flex items-center">
                <Target className="w-5 h-5 mr-2 text-indigo-600" />
                Your Progress
              </h3>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-indigo-600">{stats.total_enrollments || 0}</p>
                  <p className="text-xs text-slate-500">Courses</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-teal-600">{stats.quizzes_taken || 0}</p>
                  <p className="text-xs text-slate-500">Quizzes</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{stats.certificates_count || 0}</p>
                  <p className="text-xs text-slate-500">Certificates</p>
                </div>
              </div>
            </Card>

            {/* Recent Activity */}
            {stats.recent_transactions && stats.recent_transactions.length > 0 && (
              <Card className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                <h3 className="font-bold text-slate-900 mb-3">Recent Activity</h3>
                <div className="space-y-2">
                  {stats.recent_transactions.map((transaction, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div className="flex-1">
                        <p className="text-sm text-slate-900">{transaction.description}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(transaction.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="text-sm font-bold text-teal-600">+{transaction.points}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Menu Items */}
            <Card className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              {menuItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <button
                    key={index}
                    onClick={item.onClick}
                    className={`w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors ${
                      index !== menuItems.length - 1 ? 'border-b border-slate-100' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-slate-600" />
                      </div>
                      <span className="font-medium text-slate-900">{item.label}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {item.badge && (
                        <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                          {item.badge}
                        </span>
                      )}
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </button>
                );
              })}
            </Card>

            {/* Logout Button */}
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="w-full rounded-xl h-12 border-red-200 text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Logout
            </Button>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard" className="space-y-4">
            {/* Your Rank Card */}
            <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm">Your Rank</p>
                  <p className="text-3xl font-bold">#{leaderboard.my_rank || '-'}</p>
                </div>
                <div className="text-right">
                  <p className="text-white/80 text-sm">Your Points</p>
                  <p className="text-2xl font-bold">{leaderboard.my_points || 0}</p>
                </div>
              </div>
            </Card>

            {/* Leaderboard List */}
            <Card className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h3 className="font-bold text-slate-900 flex items-center">
                  <Trophy className="w-5 h-5 mr-2 text-amber-500" />
                  Top Students
                </h3>
              </div>
              <div className="divide-y divide-slate-100">
                {leaderboard.leaderboard && leaderboard.leaderboard.length > 0 ? (
                  leaderboard.leaderboard.map((student, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center justify-between p-4 ${
                        student.user_id === user?.id ? 'bg-indigo-50' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          idx === 0 ? 'bg-amber-500 text-white' :
                          idx === 1 ? 'bg-slate-400 text-white' :
                          idx === 2 ? 'bg-amber-700 text-white' :
                          'bg-slate-200 text-slate-600'
                        }`}>
                          {idx < 3 ? (
                            <Medal className="w-4 h-4" />
                          ) : (
                            student.rank
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {student.user_name}
                            {student.user_id === user?.id && (
                              <span className="text-xs text-indigo-600 ml-2">(You)</span>
                            )}
                          </p>
                          <p className="text-xs text-slate-500">Rank #{student.rank}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-indigo-600">{student.total_points}</p>
                        <p className="text-xs text-slate-500">points</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <Trophy className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500">No rankings yet</p>
                    <p className="text-sm text-slate-400">Enroll in courses to start earning points!</p>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Certificates Tab */}
          <TabsContent value="certificates" className="space-y-4">
            {stats.certificates && stats.certificates.length > 0 ? (
              stats.certificates.map((cert, idx) => (
                <Card 
                  key={idx}
                  className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white">
                    <div className="flex items-center justify-between">
                      <Award className="w-10 h-10" />
                      <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
                        {cert.certificate_number}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-slate-900 mb-1">{cert.course_title}</h3>
                    <p className="text-sm text-slate-500 mb-3">
                      Issued: {new Date(cert.issued_at).toLocaleDateString()}
                    </p>
                    <Button
                      onClick={() => handleDownloadCertificate(cert)}
                      size="sm"
                      className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Certificate
                    </Button>
                  </div>
                </Card>
              ))
            ) : (
              <Card className="bg-white rounded-xl p-8 text-center shadow-sm border border-slate-100">
                <Award className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="font-bold text-slate-900 mb-2">No Certificates Yet</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Complete courses to earn certificates!
                </p>
                <Button
                  onClick={() => navigate('/courses')}
                  className="rounded-full"
                >
                  Browse Courses
                </Button>
              </Card>
            )}

            {/* How to Earn Certificates */}
            <Card className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <h3 className="font-bold text-slate-900 mb-3">How to Earn Certificates</h3>
              <div className="space-y-2 text-sm text-slate-600">
                <p className="flex items-start">
                  <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">1</span>
                  Enroll in a course
                </p>
                <p className="flex items-start">
                  <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">2</span>
                  Complete all lessons and quizzes
                </p>
                <p className="flex items-start">
                  <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">3</span>
                  Mark course as complete to receive your certificate
                </p>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <BottomNavigation />
    </div>
  );
}
